import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import logo from '../assets/transparent-logo.png';

const Navbar = ({ onToggleLanguage, currentLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleNavItemClick = () => {
    setIsOpen(false);
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-[#0f1729]/95 backdrop-blur-sm shadow-lg' : 'bg-[#0f1729]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src={logo}
              alt="APED Investment Logo"
              className="h-10 w-auto sm:h-12"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <a href="#hero" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.home')}</a>
            <a href="#about" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.about')}</a>
            <a href="#vision-mission" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.visionMission')}</a>
            <a href="#services" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.values')}</a>
            <a href="#why-invest" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.projects')}</a>
            <a href="#gallery" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.gallery')}</a>
            <a href="#contact" className="text-white hover:text-[#b4a35e] transition-colors duration-300 font-medium">{t('navbar.contact')}</a>

            {/* Desktop Language Switcher */}
            <button
              onClick={onToggleLanguage}
              className="flex items-center space-x-2 px-4 py-2 rounded-full bg-[#2a2f4a] hover:bg-[#353a5a] transition-colors duration-300"
            >
              <Globe className="h-4 w-4 text-[#b4a35e]" />
              <span className="text-white font-medium text-sm">
                {currentLanguage === 'en' ? 'EN' : 'FR'}
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center space-x-3">
            {/* Mobile Language Switcher */}
            <button
              onClick={onToggleLanguage}
              className="flex items-center space-x-1 px-2 py-1 rounded-full bg-[#2a2f4a] hover:bg-[#353a5a] transition-colors duration-300"
              aria-label="Toggle language"
            >
              <Globe className="h-3 w-3 text-[#b4a35e]" />
              <span className="text-white font-medium text-xs">
                {currentLanguage === 'en' ? 'EN' : 'FR'}
              </span>
            </button>

            {/* Hamburger Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-[#2a2f4a] transition-colors duration-300"
              aria-expanded={isOpen}
              aria-label="Toggle menu"
            >
              <div className="w-5 h-5 flex flex-col justify-between items-center">
                <span className={`w-full h-0.5 bg-white transform transition-all duration-300 ${
                  isOpen ? 'rotate-45 translate-y-2' : ''
                }`} />
                <span className={`w-full h-0.5 bg-white transition-all duration-300 ${
                  isOpen ? 'opacity-0' : ''
                }`} />
                <span className={`w-full h-0.5 bg-white transform transition-all duration-300 ${
                  isOpen ? '-rotate-45 -translate-y-2' : ''
                }`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />
      )}

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed top-16 left-0 right-0 z-50 bg-[#0f1729] shadow-xl transition-all duration-300 ease-in-out ${
        isOpen
          ? 'opacity-100 visible transform translate-y-0'
          : 'opacity-0 invisible transform -translate-y-4'
      }`}>
        <div className="px-4 py-6 space-y-4">
          <a href="#hero" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.home')}</a>
          <a href="#about" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.about')}</a>
          <a href="#vision-mission" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.visionMission')}</a>
          <a href="#services" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.values')}</a>
          <a href="#why-invest" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.projects')}</a>
          <a href="#gallery" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.gallery')}</a>
          <a href="#contact" onClick={handleNavItemClick} className="block px-4 py-3 text-white hover:bg-[#2a2f4a] rounded-lg transition-colors duration-300 font-medium">{t('navbar.contact')}</a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;